import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import Modal from "@insite/mobius/Modal";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { CustomRailValidation, ValidationResult } from "@thkStore/Components/CustomProducts/Utils/CustomRailValidation";
import { calculateRail } from "@thkStore/Services/RailConfiguratorService";
import { CalculateRailSource } from "@thkStore/Types/CalculateRailParameter";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import styled from "styled-components";

export interface CustomIncludeModalConfig {
    headerText: string;
    image: string;
    unitLabel: string;
    holeDiameterLabel: string;
    lengthMinLabel: string;
    lengthMaxLabel: string;
    lengthIncrementLabel: string;
    g1MinLabel: string;
    g1MaxLabel: string;
    g1IncrementLabel: string;
    g2MinLabel: string;
    g2MaxLabel: string;
    g2IncrementLabel: string;
    cancelButtonLabel: string;
    confirmButtonLabel: string;
}

export interface RailValidationConfig {
    minimumLength: number;
    maximumLength: number;
    lengthIncrement: number;
    g1Minimum: number;
    g1Maximum: number;
    g1Increment: number;
    g2Minimum: number;
    g2Maximum: number;
    g2Increment: number;
    holePitchF: number;
    holeDiameter: number;
}

interface CustomRailDialogProps {
    isOpen: boolean;
    config: CustomIncludeModalConfig;
    validationConfig: RailValidationConfig;
    customProductTemplateId: string;
    parentProductId: string;
    source?: CalculateRailSource;
    preconfiguredValues?: PreconfiguredValues | null;
    onConfirm: (values: CustomIncludeValues) => void;
    onCancel: () => void;
}

export interface PreconfiguredValues {
    localId: string;
    length: number;
    g1: number;
    g2: number;
}

export interface CustomIncludeValues extends PreconfiguredValues {
    unitPrice: number;
    weight: number;
    partNo: string;
    daysToShip: string;
}

const HeroImage = styled.img`
    width: 100%;
    max-height: 160px;
    object-fit: cover;
    margin-bottom: 19px;
`;

const InputsContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 3px 46px;

    & > [class^="InputGroup-sc-"] {

        &:nth-child(2) {
            grid-column-start: 1;
        }
    }
`;

const InputGroup = styled.div`
    margin-bottom: 16px;
`;

const InfoLabelTop = styled(Typography)`
    display: block;
    margin-bottom: 8px;
`;

const InfoLabel = styled(Typography)`
    display: block;
    margin-top: 8px;
    color: ${({ theme }) => theme.colors.danger.main};
`;

const ErrorLabel = styled(Typography)`
    display: block;
    margin-top: 8px;
    color: ${({ theme }) => theme.colors.danger.main};
`;

const ButtonRow = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 30px;
    margin-top: 30px;

    @media (min-width:768px){
        padding: 0 50px;
    }
`;

const CustomRailDialog: React.FC<CustomRailDialogProps> = ({
    isOpen,
    config,
    validationConfig,
    customProductTemplateId,
    parentProductId,
    source,
    preconfiguredValues,
    onConfirm,
    onCancel,
}) => {
    const toasterContext = useContext(ToasterContext);
    const [length, setLength] = useState<number | "">(preconfiguredValues?.length ?? "");
    const [g1, setG1] = useState<number | "">(preconfiguredValues?.g1 ?? "");
    const [g2, setG2] = useState<number | "">(preconfiguredValues?.g2 ?? "");

    useEffect(() => {
        if (preconfiguredValues) {
            setLength(preconfiguredValues.length);
            setG1(preconfiguredValues.g1);
            setG2(preconfiguredValues.g2);
        }
    }, [preconfiguredValues]);

    const railLength = typeof length === "number" ? length : 0;
    const g1Value = typeof g1 === "number" ? g1 : 0;
    const g2Value = typeof g2 === "number" ? g2 : 0;

    const allFieldsFilled = length !== "" && g1 !== "" && g2 !== "";

    const imgUrl = config.image ? new URL(config.image) : null;

    const validation: ValidationResult | undefined = useMemo(() => {       
        return CustomRailValidation(
            railLength,
            g1Value,
            g2Value,
            validationConfig.holePitchF,
            validationConfig.holeDiameter,
            validationConfig.minimumLength,
            validationConfig.maximumLength,
            validationConfig.lengthIncrement,
            validationConfig.g1Minimum,
            validationConfig.g1Maximum,
            validationConfig.g1Increment,
            validationConfig.g2Minimum,
            validationConfig.g2Maximum,
            validationConfig.g2Increment,
        );
    }, [railLength, g1Value, g2Value, allFieldsFilled, validationConfig]);

    const lengthErrors = validation?.errors.filter(e => e.field === "length") ?? [];
    const g1Errors = validation?.errors.filter(e => e.field === "g1") ?? [];
    const g2Errors = validation?.errors.filter(e => e.field === "g2") ?? [];
    const geometryErrors = validation?.errors.filter(e => e.field === "geometry") ?? [];
    const isValid = validation?.isValid === true;

    const handleNumericChange = useCallback(
        (setter: React.Dispatch<React.SetStateAction<number | "">>) => (event: React.ChangeEvent<HTMLInputElement>) => {
            const raw = event.target.value;
            if (raw === "") {
                setter("");
                return;
            }
            const parsed = parseFloat(raw);
            if (!Number.isNaN(parsed)) {
                setter(parsed);
            }
        },
        [],
    );

    const handleConfirm = useCallback(async () => {
        try {
            const result = await calculateRail({
                productId: customProductTemplateId,
                parentProductId,
                railLength,
                g1: g1 !== "" ? g1 : undefined,
                g2: g2 !== "" ? g2 : undefined,
                source,
            });
            onConfirm({
                localId: preconfiguredValues?.localId ?? `${Date.now()}`, // generate a unique localId for this custom include item
                length: length as number,
                g1: g1 as number,
                g2: g2 as number,
                unitPrice: result.unitPrice || 0,
                weight: result.productWeight || 0,
                partNo: result.partNo || "",
                daysToShip: result.daysToShip || "",
            });

        } catch(error) {
            console.error("Error calculating rail:", error);
            toasterContext.addToast({
                            body: "An error occurred while calculating the custom product. Please try again.",
                            messageType: "danger",
                        });            
        } finally {            
            clearState();
        }
    }, [length, g1, g2, onConfirm, preconfiguredValues]);

    const handleCancel = useCallback(() => {
        clearState();
        onCancel();
    }, [onCancel]);

    const clearState = () => {
        setLength("");
        setG1("");
        setG2("");
    }

    return (
        <Modal
            headline={config.headerText || translate("Custom Include")}
            isOpen={isOpen}
            handleClose={handleCancel}
            sizeVariant="medium"
            closeOnEsc
            closeOnScrimClick={false}
        >

            {config.unitLabel && <InfoLabelTop>{config.unitLabel}</InfoLabelTop>}
            {config.holeDiameterLabel && <InfoLabelTop>{config.holeDiameterLabel} {validationConfig.holePitchF}</InfoLabelTop>}
        
            {config.image && <HeroImage src={`${imgUrl?.pathname}${imgUrl?.search}`} alt="Custom Length" />}

            <InputsContainer>
            
                <InputGroup>
                    <TextField
                        label={translate("Length(L)*")}
                        type="number"
                        value={length === "" ? "" : String(length)}
                        onChange={handleNumericChange(setLength)}
                        data-test-selector="customIncludeLength"
                        placeholder="Enter Keywords"
                    />
                    {config.lengthMinLabel && <InfoLabel>{config.lengthMinLabel} {validationConfig.minimumLength}</InfoLabel>}
                    {config.lengthMaxLabel && <InfoLabel>{config.lengthMaxLabel} {validationConfig.maximumLength}</InfoLabel>}
                    {config.lengthIncrementLabel && <InfoLabel>{config.lengthIncrementLabel} {validationConfig.lengthIncrement}</InfoLabel>}
                    {lengthErrors.map((err, i) => <ErrorLabel key={i}>{err.message}</ErrorLabel>)}
                </InputGroup>

                <InputGroup>
                    <TextField
                        label="G1"
                        type="number"
                        value={g1 === "" ? "" : String(g1)}
                        onChange={handleNumericChange(setG1)}
                        data-test-selector="customIncludeG1"
                    />
                    {config.g1MinLabel && <InfoLabel>{config.g1MinLabel} {validationConfig.g1Minimum}</InfoLabel>}
                    {config.g1MaxLabel && <InfoLabel>{config.g1MaxLabel} {validationConfig.g1Maximum}</InfoLabel>}
                    {config.g1IncrementLabel && <InfoLabel>{config.g1IncrementLabel} {validationConfig.g1Increment}</InfoLabel>}
                    {g1Errors.map((err, i) => <ErrorLabel key={i}>{err.message}</ErrorLabel>)}
                </InputGroup>

                <InputGroup>
                    <TextField
                        label="G2"
                        type="number"
                        value={g2 === "" ? "" : String(g2)}
                        onChange={handleNumericChange(setG2)}
                        data-test-selector="customIncludeG2"
                    />
                    {config.g2MinLabel && <InfoLabel>{config.g2MinLabel} {validationConfig.g2Minimum}</InfoLabel>}
                    {config.g2MaxLabel && <InfoLabel>{config.g2MaxLabel} {validationConfig.g2Maximum}</InfoLabel>}
                    {config.g2IncrementLabel && <InfoLabel>{config.g2IncrementLabel} {validationConfig.g2Increment}</InfoLabel>}
                    {g2Errors.map((err, i) => <ErrorLabel key={i}>{err.message}</ErrorLabel>)}
                </InputGroup>
                
            </InputsContainer>

            {geometryErrors.length > 0 && (
                <InputGroup>
                    {geometryErrors.map((err, i) => <ErrorLabel key={i}>{err.message}</ErrorLabel>)}
                </InputGroup>
            )}

            <ButtonRow>
                <Button variant="secondary" onClick={handleCancel} data-test-selector="customIncludeCancel">
                    {config.cancelButtonLabel || translate("Cancel")}
                </Button>
                <Button color="primary" onClick={handleConfirm} disabled={!isValid} data-test-selector="customIncludeConfirm">
                    {config.confirmButtonLabel || translate("Confirm")}
                </Button>
            </ButtonRow>

        </Modal>
    );
};

export default CustomRailDialog;
